import { useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import { toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { sendEncounterServices } from "../services/encounter-services-send";

type SendServicesOptions = {
  /**
   * Runs before the first charge is sent. Return false to stop the send.
   *
   * Anything the send needs written first belongs here rather than at the call site, so it runs under
   * the same in-flight guard as the send itself.
   */
  beforeSend?: () => Promise<boolean>;
};

type UseSendEncounterServices = {
  /**
   * True for the whole action, `beforeSend` included, so the button cannot start a second one on top
   * of it.
   */
  isSending: boolean;
  sendServices: (options?: SendServicesOptions) => Promise<void>;
};

/**
 * Sends the encounter's Services and tells the biller how it went.
 *
 * Only the encounter's id and the reason reach Sentry, and no toast names a code or a client: what an
 * encounter was billed for is clinical, and a failure to send it is an operational problem.
 */
const reportFailure = (encounterId: string, error: unknown) => {
  captureExceptionWithoutPhi("Failed to send the encounter's services to billing", error, {
    encounterId,
  });
  toast({
    title: "Failed to send to services",
    message:
      "The billing codes were not sent — try again, or contact support if the error persists.",
    type: "error",
    autoClose: 5000,
  });
};

/**
 * The billing tab's `Send to Services` action: the write, and everything the biller is told about it.
 *
 * Four outcomes are worth distinguishing, and the counts the service returns are what separate them.
 * An encounter with no captured codes has nothing to send, and saying "sent" would be a lie about an
 * encounter that was never billed for anything. One whose codes were all sent already is the second
 * click, or a colleague who got there first. Both are ordinary, so neither is an error — but neither
 * is a success the biller should read as "that went just now" either.
 */
function useSendEncounterServices(encounterId: string | undefined): UseSendEncounterServices {
  const medplum = useMedplum();
  const [isSending, setIsSending] = useState(false);
  // A ref as well as the state, because the state is what the button reads and it only gets there on
  // the next render. Two clicks in the same tick both see `isSending` false, so the ref is what stops
  // the second one, and it is claimed before the first await rather than after `beforeSend`.
  const isSendingRef = useRef(false);

  const sendServices = async ({ beforeSend }: SendServicesOptions = {}) => {
    if (!encounterId || isSendingRef.current) return;

    isSendingRef.current = true;
    setIsSending(true);

    try {
      if (beforeSend && !(await beforeSend())) return;

      const result = await sendEncounterServices({ medplum, encounterId });

      if (!result.ok) {
        const reason = result.details ? `${result.reason}: ${result.details}` : result.reason;
        reportFailure(encounterId, new Error(reason));
        return;
      }

      const { sentCount, serviceCount } = result.data;

      if (!serviceCount) {
        toast({
          title: "Nothing to send",
          message: "This encounter has no billing codes to send to services.",
          type: "warning",
          autoClose: 5000,
        });
        return;
      }

      // The codes are with billing and only the encounter's queue label is missing. Worth a toast
      // rather than Sentry alone, because sending again is both the fix and something the biller can
      // do from the button they just pressed.
      if (result.servicesStatusTagFailure) {
        captureExceptionWithoutPhi(
          "Sent the encounter's services but failed to tag it as fully sent",
          new Error(result.servicesStatusTagFailure),
          { encounterId },
        );
        toast({
          title: "Sent, but this encounter still reads as unsent",
          message:
            "The billing codes went to services. Send again so the encounter stops showing up as missing services.",
          type: "warning",
          autoClose: 5000,
        });
        return;
      }

      if (!sentCount) {
        toast({
          title: "Already sent",
          message: "This encounter's billing codes had already been sent to services.",
          type: "info",
          autoClose: 3000,
        });
        return;
      }

      toast({
        title: "Sent to services",
        message: `${sentCount} billing ${sentCount === 1 ? "code" : "codes"} went to services.`,
        type: "success",
        autoClose: 3000,
      });
    } catch (error) {
      reportFailure(encounterId, error);
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  return { isSending, sendServices };
}

export default useSendEncounterServices;
